import React, { useEffect, useState } from 'react';
import {
    Box, TextField, Button, IconButton, Typography, Alert,
    Paper, MenuItem, CircularProgress
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import CreateIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
// import { toast } from 'react-toastify';
import toast from 'react-hot-toast';

import Farmer360logo from '../../assets/Farmer360logo.png';

import {
    loginWithOTPAction,
    clearLoginWithOTPAction,
    verifyOtpAction,
    getAllTenantsAction,
} from '../../store/actions';

const OtpLogin = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Always clear localStorage on mount to ensure fresh login state
    useEffect(() => {
        localStorage.clear();
    }, []);


    // OTP send state
    const otpState = useSelector(state => state.loginWithOTPReducer);
    // OTP verify state
    const otpVerifyState = useSelector(state => state.verifyLoginOtpReducer);
    // Tenants state (fix: use correct reducer and data path)
    const tenants = useSelector(state => state.getAllTenantsReducer?.successData?.data || []);
    const tenantsLoading = useSelector(state => state.getAllTenantsReducer?.loading || false);

    const [selectedCompany, setSelectedCompany] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [timer, setTimer] = useState(60);
    const [otpExpired, setOtpExpired] = useState(false);

    console.log("otpVerifyState", otpVerifyState);

     
    // ✅ Load tenants
    useEffect(() => {
        dispatch(getAllTenantsAction());
    }, [dispatch]);

    // ✅ Timer logic
    useEffect(() => {
        if (!otpSent) return;

        if (timer <= 0) {
            setOtpExpired(true);
            return;
        }

        const interval = setInterval(() => {
            setTimer((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [otpSent, timer]);

    // ✅ OTP SEND HANDLER
    useEffect(() => {
        if (otpState?.successData) {
            toast.success("OTP sent successfully");
            setOtpSent(true);
            setTimer(60);
            setOtpExpired(false);
            dispatch(clearLoginWithOTPAction());
        }

        if (otpState?.error) {
            toast.error(otpState.error?.message || "Failed to send OTP");
            setOtpSent(false);
            dispatch(clearLoginWithOTPAction());
        }
    }, [otpState, dispatch]);

    // ✅ OTP VERIFY HANDLER
    useEffect(() => {
        if (otpVerifyState?.successData) {
            toast.success("OTP verified successfully");

            // The API response structure:
            // { data: { userId, name, mobile, email, role, status, accessToken } }
            const user = otpVerifyState.successData?.data;
            if (user) {
                const details = {
                    id: user.userId || null,
                    name: user.name || null,
                    mobile: user.mobile || null,
                    email: user.email || null,
                    role: user.role || null,
                    status: user.status || null,
                    token: user.accessToken || null,
                };
                localStorage.setItem("userDetails", JSON.stringify(details));
                localStorage.setItem("isAuthenticated", "true");

                // Force localStorage write before navigation
                setTimeout(() => {
                    if (details.role === "ADMIN" || details.role === "ZM" || details.role === "AE") {
                        navigate("/masterData/dashboard");
                    } else if (details.role === "RM" || details.role === "AM") {
                        navigate("/amrm");
                    } else if (details.role === "SO" || details.role === "MDO") {
                        navigate("/SOorMDO/dashboard");
                    } else {
                        navigate("/");
                    }
                }, 100);
            }
            dispatch(clearLoginWithOTPAction());
        }

        if (otpVerifyState?.error) {
            toast.error(
                otpVerifyState.error?.message ||
                "Invalid OTP, please try again"
            );
        }
    }, [otpVerifyState, dispatch, navigate]);

    // ✅ Formik
    const formik = useFormik({
        initialValues: { phoneNumber: '', otp: '' },
        validationSchema: Yup.object({
            phoneNumber: Yup.string()
                .required('Phone number is required')
                .matches(/^\d{10}$/, 'Must be exactly 10 digits'),
            otp: otpSent
                ? Yup.string()
                    .required('OTP required')
                    .matches(/^\d{4}$/, 'Must be 4 digits')
                : Yup.string(),
        }),
        onSubmit: (values) => {
            if (!otpSent) {
                dispatch(loginWithOTPAction({
                    identifier: selectedCompany,
                    mobile: values.phoneNumber,
                }));
            } else {
                dispatch(verifyOtpAction({
                    identifier: selectedCompany,
                    mobile: values.phoneNumber,
                    otp: values.otp,
                }));
            }
        },
    });

    const resetOtp = () => {
        formik.resetForm();
        setOtpSent(false);
        setTimer(60);
        setOtpExpired(false);
    };

    const resendOtp = () => {
        dispatch(loginWithOTPAction({
            identifier: selectedCompany,
            mobile: formik.values.phoneNumber,
        }));
        setTimer(60);
        setOtpExpired(false);
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f8f9fa'
        }}>
            <Paper sx={{
                display: 'flex',
                width: '900px',
                borderRadius: 3,
                overflow: 'hidden'
            }}>

                {/* LEFT */}
                <Box sx={{
                    width: '50%',
                    background: '#E2F0FF',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <img src={Farmer360logo} alt="logo" width="60%" />
                </Box>

                {/* RIGHT */}
                <Box sx={{ width: '50%', p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton onClick={() => navigate('/login')}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Typography variant="h5" mb={2}>
                        OTP Login
                    </Typography>

                    {/* Tenant */}
                    {tenantsLoading ? (
                        <CircularProgress />
                    ) : (
                        <TextField
                            select fullWidth label="Select Company"
                            value={selectedCompany}
                            onChange={(e) => setSelectedCompany(e.target.value)}
                            disabled={otpSent}
                            sx={{ mb: 2 }}
                        >
                            {tenants.map(t => (
                                <MenuItem key={t.id} value={t.identifier}>
                                    {t.companyName || t.identifier}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    {/* Phone */}
                    <Box display="flex">
                        <TextField
                            fullWidth label="Phone Number"
                            value={formik.values.phoneNumber}
                            disabled={otpSent}
                            onChange={(e) =>
                                formik.setFieldValue(
                                    'phoneNumber',
                                    e.target.value.replace(/\D/g, '').slice(0, 10)
                                )
                            }
                        />
                        {otpSent && (
                            <IconButton onClick={resetOtp}>
                                <CreateIcon />
                            </IconButton>
                        )}
                    </Box>

                    {/* SEND OTP */}
                    {!otpSent && (
                        <Button
                            fullWidth
                            variant="contained"
                            sx={{ mt: 2 }}
                            onClick={formik.handleSubmit}
                            disabled={!selectedCompany || !formik.values.phoneNumber}
                        >
                            Send OTP
                        </Button>
                    )}

                    {/* OTP INPUT */}
                    {otpSent && !otpExpired && (
                        <>
                            <Alert sx={{ mt: 2 }}>
                                OTP sent to {formik.values.phoneNumber}
                            </Alert>

                            <TextField
                                fullWidth
                                label="Enter OTP"
                                sx={{ mt: 2 }}
                                value={formik.values.otp}
                                onChange={(e) =>
                                    formik.setFieldValue(
                                        'otp',
                                        e.target.value.replace(/\D/g, '').slice(0, 4)
                                    )
                                }
                            />

                            <Typography mt={1}>
                                Expires in {timer}s
                            </Typography>

                            <Button
                                fullWidth
                                variant="contained"
                                sx={{ mt: 2 }}
                                onClick={formik.handleSubmit}
                            >
                                Verify OTP
                            </Button>
                        </>
                    )}

                    {/* EXPIRED */}
                    {otpExpired && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            OTP expired
                            <Button onClick={resendOtp}>Resend</Button>
                        </Alert>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default OtpLogin;